import React, { useEffect, useState } from "react";
import { Spin, Input, Form, Button, message } from "antd";
import styled from "styled-components";
import moment from "moment";
import { fetchSalesGoals, setUserTargets } from "../../services/dashboardService";
import { getAllTeams } from "../../services/teamsService";
import logger, { Logger } from '../../utils/logger';
import PopupComponent from "../../components/PopupComponent";
import { useSelector } from "react-redux";

// Types
interface TargetData {
  name?: string;
  target: number;
  progress: number;
  percentage: number;
}

interface Lead {
  id: string;
  name: string;
  assignedTo: string;
  salesDate: string;
  salesAmount: number;
  soldById: string;
  soldByName: string;
}

interface EntityTargets {
  sales_amount?: TargetData;
  calls?: TargetData;
}

interface UserTarget {
  target: number;
  progress: number;
  percentage: number;
  name: string;
  _id: string;
  leads?: Lead[]; // 🎯 NEW: Added leads property
}

interface UserTargetSection {
  users: UserTarget[];
}

// Define the possible response structures
interface OrganizationResponse {
  month: string;
  organization?: EntityTargets;
  employees?: {
    [key: string]: EntityTargets;
  };
}

interface TeamResponse {
  month: string;
  [teamId: string]: TeamData | string;
}

interface TeamData {
  sales_amount?: TargetData;
  calls?: TargetData;
  employees?: {
    [key: string]: EntityTargets;
  };
}

interface SingleUserResponse {
  month: string;
  sales_amount?: UserTargetSection;
  calls?: UserTargetSection;
}

type SalesTargetsResponse = OrganizationResponse | TeamResponse | SingleUserResponse;

// 🎯 FIXED Grid Container for responsive layout
const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
  
  /* Force 2 columns on larger screens */
  @media (min-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  /* Force 3 columns on very large screens */
  @media (min-width: 1400px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

// 🎯 SIMPLIFIED: Just a button for leads instead of inline display
const ViewDetailsButton = styled.button`
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// 🎯 NEW: Leads popup table styling
const LeadsPopupTable = styled.div`
  .table-container {
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
  }
  
  .table-header {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 12px;
    padding: 16px;
    background: #f8fafc;
    font-size: 13px;
    font-weight: 700;
    color: #374151;
    border-bottom: 2px solid #e5e7eb;
    position: sticky;
    top: 0;
    z-index: 1;
  }
  
  .table-row {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 12px;
    padding: 16px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 14px;
    transition: all 0.2s ease;
    
    &:hover {
      background: #f8fafc;
    }
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  .lead-name {
    font-weight: 600;
    color: #1e293b;
  }
  
  .sales-amount {
    font-weight: 700;
    color: #059669;
  }
  
  .sold-by {
    color: #64748b;
    font-size: 13px;
  }
  
  .sales-date {
    color: #64748b;
    font-size: 13px;
  }
`;

const LeadsSummaryCard = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;
  
  .stat-card {
    text-align: center;
    padding: 16px;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    
    .label {
      font-size: 12px;
      color: #64748b;
      text-transform: uppercase;
      font-weight: 600;
      margin-bottom: 4px;
    }
    
    .value {
      font-size: 20px;
      font-weight: 700;
      color: #1e293b;
    }
  }
`;

// 🎯 ENHANCED: Better card styling
const TargetContainer = styled.div`
  padding: 24px;
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(226, 232, 240, 0.8);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%);
  }
`;

const TargetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
  font-family: "Gilroy-Bold", sans-serif;
`;

const EntityName = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const MonthDisplay = styled.span`
  font-size: 14px;
  color: #64748b;
  font-weight: 600;
  background: #f1f5f9;
  padding: 6px 12px;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
`;

const TargetSection = styled.div`
  margin-bottom: 16px;
`;

const TargetLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const SectionTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

const PercentageText = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

const ProgressBarContainer = styled.div`
  height: 12px;
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 100%);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 8px;
  position: relative;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.06);
`;

const ProgressBarFill = styled.div<{ percentage: number }>`
  height: 100%;
  background: ${(props) => {
    if (props.percentage >= 100) {
      return 'linear-gradient(90deg, #10b981 0%, #059669 100%)';
    } else if (props.percentage >= 75) {
      return 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)';
    } else if (props.percentage >= 50) {
      return 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)';
    } else {
      return 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)';
    }
  }};
  width: ${(props) => Math.min(props.percentage, 100)}%;
  border-radius: 8px;
  transition: all 0.4s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%);
    animation: shimmer 2s infinite;
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const TargetValues = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ValueText = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #333;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  background-color: #f9f9f9;
  border-radius: 8px;
  text-align: center;
`;

const EmptyStateText = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 16px;
`;

const SetTargetButton = styled.button`
  background-color: #06402b;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  &:hover {
    background-color: #052e1f;
  }
`;

const SectionTitle2 = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
`;

// Main component
interface SalesTargetTrackingProps {
  fromDate: string;
  toDate: string;
  isOrganization: boolean;
  selectedUserId: string;
  selectedTeamId: string;
}

const SalesTargetTracking: React.FC<SalesTargetTrackingProps> = ({
  fromDate,
  toDate,
  isOrganization,
  selectedUserId,
  selectedTeamId
}) => {
  // 🎯 NEW: Redux store access for team names
  const StoreData = useSelector((state: any) => state?.rootReducers);
  
  const [loading, setLoading] = useState<boolean>(false);
  const [targetsData, setTargetsData] = useState<SalesTargetsResponse | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [targetUserId, setTargetUserId] = useState<string>("");
  const [targetUserName, setTargetUserName] = useState<string>("");
  const [salesTarget, setSalesTarget] = useState<string>("");
  const [callsTarget, setCallsTarget] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [form] = Form.useForm();
  
  // 🎯 NEW: Leads popup state
  const [leadsPopupOpen, setLeadsPopupOpen] = useState<boolean>(false);
  const [selectedUserLeads, setSelectedUserLeads] = useState<Lead[]>([]);
  const [selectedUserName, setSelectedUserName] = useState<string>("");
  
  // 🎯 NEW: Teams state for proper naming
  const [teamMembers, setTeamMembers] = useState<Array<any>>([]);

  // Format currency values
  const formatCurrency = (value: number): string => {
    return "₹" + value.toLocaleString("en-IN");
  };

  // 🎯 NEW: Open leads popup
  const openLeadsPopup = (leads: Lead[], userName: string) => {
    setSelectedUserLeads(leads);
    setSelectedUserName(userName);
    setLeadsPopupOpen(true);
  };

  // 🎯 NEW: Calculate lead statistics for popup
  const calculateLeadStats = (leads: Lead[]) => {
    if (!leads || leads.length === 0) return null;
    
    const totalAmount = leads.reduce((sum, lead) => sum + lead.salesAmount, 0);
    const avgAmount = totalAmount / leads.length;
    
    return {
      totalLeads: leads.length,
      totalAmount,
      avgAmount,
      recentSale: leads[0]
    };
  };

  // 🎯 NEW: Fetch teams for proper naming (from LeadFilter logic)
  const fetchTeams = async () => {
    try {
      const response = await getAllTeams();
      
      if (response && response.status) {
        let tempArray = [...(response.data?.data || [])];
        
        // Add "Team" suffix to each team name
        tempArray.forEach((el: any) => {
          if (el && el.name) {
            el.showName = el.name + " - Team";
            el.originalName = el.name; // Keep original name
          }
        });

        // Add organization data
        const organizationName = StoreData?.userData?.userDetails?.organization?.name;
        
        if (organizationName) {
          const tempOrgData = {
            showName: organizationName + " - Organization",
            originalName: organizationName,
            _id: "organization",
          };
          tempArray.push(tempOrgData);
        }
        
        setTeamMembers(tempArray);
        logger.log('✅ Teams data loaded:', tempArray.length);
        
      } else {
        setTeamMembers([]);
      }
      
    } catch (error) {
      console.error('❌ Error fetching teams:', error);
      setTeamMembers([]);
    }
  };

  // 🎯 NEW: Get team name by ID
  const getTeamNameById = (teamId: string): string => {
    const team = teamMembers.find(t => t._id === teamId);
    return team?.originalName || `Team ${teamId}`;
  };

  useEffect(() => {
    fetchTargets();
  }, [fromDate, toDate, isOrganization, selectedUserId, selectedTeamId]);

  // 🎯 NEW: Fetch teams on component mount
  useEffect(() => {
    fetchTeams();
  }, [StoreData?.userData?.userDetails]);

  const fetchTargets = async () => {
    try {
      setLoading(true);
      
      const response = await fetchSalesGoals(
        fromDate,
        toDate,
        isOrganization,
        selectedUserId,
        selectedTeamId
      );
      
      if (response && response.status) {
        logger.log("Sales target data received:", response.data.data);
        setTargetsData(response.data.data);
      } else {
        logger.log("Error fetching sales targets: No data or invalid response");
        Logger.log("Error fetching sales targets: No data or invalid response");
      }
    } catch (error) {
      logger.log("Error fetching sales targets:", error);
      Logger.log("Error fetching sales targets:", error);
    } finally {
      setLoading(false);
    }
  };

  // Open popup for setting targets
  const openTargetPopup = (userId: string, name: string, currentSalesTarget?: number, currentCallsTarget?: number) => {
    setTargetUserId(userId);
    setTargetUserName(name);
    setSalesTarget(currentSalesTarget?.toString() || "0");
    setCallsTarget(currentCallsTarget?.toString() || "0");
    form.setFieldsValue({
      salesTarget: currentSalesTarget?.toString() || "0",
      callsTarget: currentCallsTarget?.toString() || "0"
    });
    setIsPopupOpen(true);
  };

  // Handle form submission
  const handleSetTargets = async () => {
    try {
      await form.validateFields();
      
      if (!targetUserId) {
        message.error("No user selected for target setting");
        return;
      }
      
      setSubmitting(true);
      logger.log("Setting targets for user:", targetUserId);
      Logger.log("Setting targets for user:", targetUserId);
      
      const salesTargetValue = salesTarget ? parseFloat(salesTarget) : 0;
      const callsTargetValue = callsTarget ? parseFloat(callsTarget) : 0;
      
      const response = await setUserTargets(
        targetUserId,
        salesTargetValue,
        callsTargetValue
      );
      
      if (response && response.data) {
        message.success("Targets set successfully");
        setIsPopupOpen(false);
        fetchTargets();
      } else {
        message.error("Failed to set targets");
      }
    } catch (error) {
      logger.log("Error setting targets:", error);
      Logger.log("Error setting targets:", error);
      message.error("An error occurred while setting targets");
    } finally {
      setSubmitting(false);
    }
  };

  // Render progress section (Sales or Calls)
  const renderProgressSection = (type: string, data?: TargetData) => {
    if (!data) return null;
    
    return (
      <TargetSection>
        <TargetLabel>
          <SectionTitle>{type}</SectionTitle>
          <PercentageText>{Math.min(data.percentage, 999)}%</PercentageText>
        </TargetLabel>
        
        <ProgressBarContainer>
          <ProgressBarFill percentage={data.percentage} />
        </ProgressBarContainer>
        
        <TargetValues>
          <ValueText>
            {type === "Sales" ? formatCurrency(data.progress) : `${data.progress} calls`}
          </ValueText>
          <ValueText>
            {type === "Sales" ? formatCurrency(data.target) : `${data.target} calls`}
          </ValueText>
        </TargetValues>
      </TargetSection>
    );
  };

  // Helper function to convert team/other data to EntityTargets
  const convertToEntityTargets = (data: any): EntityTargets => {
    if (typeof data === 'string') {
      return {};
    }

    return {
      sales_amount: data.sales_amount,
      calls: data.calls
    };
  };

  // 🎯 ENHANCED: Render a target card for an entity (now with View Details button)
  const renderTargetCard = (name: string, data: EntityTargets, userId?: string, userWithLeads?: UserTarget): JSX.Element => {
    const hasSalesTarget = !!data.sales_amount?.target;
    const hasCallsTarget = !!data.calls?.target;
    const needsTargetSetting = (!hasSalesTarget && !hasCallsTarget);
    
    const currentSalesTarget = data.sales_amount?.target || 0;
    const currentCallsTarget = data.calls?.target || 0;
    
    // Check if user has leads to show View Details button
    const hasLeads = userWithLeads && userWithLeads.leads && userWithLeads.leads.length > 0;
    
    return (
      <TargetContainer>
        <TargetHeader>
          <EntityName>{name}</EntityName>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <MonthDisplay>{targetsData?.month}</MonthDisplay>
            {userId && (
              <Button 
                type="primary" 
                size="small"
                onClick={() => openTargetPopup(userId, name, currentSalesTarget, currentCallsTarget)}
                style={{ 
                  backgroundColor: needsTargetSetting ? '#06402b' : '#1890ff',
                  borderColor: needsTargetSetting ? '#06402b' : '#1890ff'
                }}
              >
                {needsTargetSetting ? 'Set Target' : 'Edit Target'}
              </Button>
            )}
          </div>
        </TargetHeader>
        
        {renderProgressSection("Sales", data.sales_amount)}
        {renderProgressSection("Calls", data.calls)}
        
        {/* 🎯 NEW: Simple View Details button instead of inline leads */}
        {hasLeads && (
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <ViewDetailsButton
              onClick={() => openLeadsPopup(userWithLeads.leads!, name)}
            >
              <span>📊</span>
              View {userWithLeads.leads!.length} Lead{userWithLeads.leads!.length !== 1 ? 's' : ''} Details
            </ViewDetailsButton>
          </div>
        )}
      </TargetContainer>
    );
  };

  // Render no targets message
  const renderEmptyState = () => {
    return (
      <EmptyState>
        <EmptyStateText>No targets set</EmptyStateText>
        {selectedUserId && (
          <SetTargetButton onClick={() => openTargetPopup(selectedUserId, "Selected User")}>
            Set Targets
          </SetTargetButton>
        )}
        {!selectedUserId && (
          <SetTargetButton onClick={() => message.info("Please select a user to set targets")}>
            Set Targets
          </SetTargetButton>
        )}
      </EmptyState>
    );
  };

  // Helper to check if there are any targets set
  const hasTargets = (): boolean => {
    logger.log("Checking if targets exist in data:", targetsData);
    if (!targetsData) return false;
    
    if ('organization' in targetsData && targetsData.organization) {
      logger.log("Found organization format data");
      return true;
    }
    
    if ('employees' in targetsData && targetsData.employees && Object.keys(targetsData.employees).length > 0) {
      logger.log("Found employees in organization format");
      return true;
    }
    
    const potentialTeamIds = Object.keys(targetsData).filter(key => 
      key !== 'month' && key !== 'organization' && 
      key !== 'employees' && key !== 'sales_amount' && key !== 'calls'
    );
    
    if (potentialTeamIds.length > 0) {
      logger.log("Found team format data with IDs:", potentialTeamIds);
      return true;
    }
    
    if ('sales_amount' in targetsData && 
        targetsData.sales_amount && 
        typeof targetsData.sales_amount !== 'string' &&
        'users' in targetsData.sales_amount && 
        targetsData.sales_amount.users.length > 0) {
      logger.log("Found single user format (sales_amount)");
      return true;
    }
    
    if ('calls' in targetsData && 
        targetsData.calls && 
        typeof targetsData.calls !== 'string' &&
        'users' in targetsData.calls && 
        targetsData.calls.users.length > 0) {
      logger.log("Found single user format (calls)");
      return true;
    }
    
    logger.log("No targets found in data");
    return false;
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "30px" }}>
        <Spin tip="Loading targets..." />
      </div>
    );
  }

  return (
    <div>
      <SectionTitle2>Sales Target & Achievement</SectionTitle2>
      
      {!hasTargets() && !loading ? (
        renderEmptyState()
      ) : (
        <>
         
          <GridContainer>
            {/* Organization level targets */}
            {targetsData && 'organization' in targetsData && targetsData.organization &&
              renderTargetCard(
                (targetsData.organization as EntityTargets).sales_amount?.name ||
                (targetsData.organization as EntityTargets).calls?.name ||
                "Organization",
                targetsData.organization as EntityTargets
              )}
            
            {/* Employee targets in organization format */}
            {targetsData && 'employees' in targetsData && targetsData.employees &&
              Object.entries(targetsData.employees).map(([id, data]) => {
                // 🎯 FIX: Create UserTarget object with leads for organization employees
                const employeeWithLeads: UserTarget = {
                  target: (data as any).sales_amount?.target || 0,
                  progress: (data as any).sales_amount?.progress || 0,
                  percentage: (data as any).sales_amount?.percentage || 0,
                  name: (data as any).sales_amount?.name || 
                        (data as any).calls?.name || 
                        `Employee ${id}`,
                  _id: id,
                  leads: (data as any).sales_amount?.leads || []
                };
                
                return (
                  <div key={id}>
                    {renderTargetCard(
                      employeeWithLeads.name,
                      data as EntityTargets,
                      id,
                      employeeWithLeads // 🎯 Pass employee with leads
                    )}
                  </div>
                );
              })}
              
            {/* Team format */}
            {targetsData && Object.keys(targetsData)
              .filter(key => key !== 'month' && key !== 'organization' && 
                     key !== 'employees' && key !== 'sales_amount' && key !== 'calls')
              .map(teamId => {
                const teamData = (targetsData as TeamResponse)[teamId];
                if (typeof teamData === 'string') return null;
                
                const teamDataTyped = teamData as TeamData;
                
                // 🎯 FIX: Get team name from fetched teams data
                const teamName = getTeamNameById(teamId);
                
                return [
                  // Team card
                  <div key={`team-${teamId}`}>
                    {renderTargetCard(
                      teamName, 
                      convertToEntityTargets(teamDataTyped)
                    )}
                  </div>,
                  
                  // 🎯 FIX: Team employees directly in grid (no wrapper div)
                  ...(teamDataTyped.employees ? 
                    Object.entries(teamDataTyped.employees).map(([empId, empData]) => {
                      // 🎯 FIX: Create UserTarget object with leads for team employees
                      const employeeWithLeads: UserTarget = {
                        target: (empData as any).sales_amount?.target || 0,
                        progress: (empData as any).sales_amount?.progress || 0,
                        percentage: (empData as any).sales_amount?.percentage || 0,
                        name: (empData as any).sales_amount?.name || 
                              (empData as any).calls?.name || 
                              `Employee ${empId}`,
                        _id: empId,
                        leads: (empData as any).sales_amount?.leads || []
                      };
                      
                      return (
                        <div key={`emp-${empId}`}>
                          {renderTargetCard(
                            employeeWithLeads.name,
                            empData as EntityTargets,
                            empId,
                            employeeWithLeads // 🎯 Pass employee with leads
                          )}
                        </div>
                      );
                    }) : []
                  )
                ];
              }).flat()}
            
            {/* 🎯 ENHANCED: Single user format with leads support */}
            {targetsData && 'sales_amount' in targetsData && 
              targetsData.sales_amount && 
              typeof targetsData.sales_amount !== 'string' &&
              'users' in targetsData.sales_amount && 
              targetsData.sales_amount.users && 
              targetsData.sales_amount.users.map((user: UserTarget, index: number) => {
                // Get calls data if it exists
                let callsData: TargetData | undefined = undefined;
                if ('calls' in targetsData && 
                    targetsData.calls && 
                    typeof targetsData.calls !== 'string' &&
                    'users' in targetsData.calls && 
                    targetsData.calls.users && 
                    targetsData.calls.users[index]) {
                  const callUser = targetsData.calls.users[index];
                  callsData = {
                    target: callUser.target,
                    progress: callUser.progress,
                    percentage: callUser.percentage,
                    name: callUser.name
                  };
                }
                
                return (
                  <div key={user._id || index}>
                    {renderTargetCard(
                      user.name || `User ${index}`,
                      {
                        sales_amount: {
                          target: user.target,
                          progress: user.progress,
                          percentage: user.percentage,
                          name: user.name
                        },
                        calls: callsData
                      },
                      user._id,
                      user // 🎯 NEW: Pass the full user object with leads
                    )}
                  </div>
                );
              })}
          </GridContainer>
        </>
      )}

      {/* Target Setting Popup - Same as before */}
      <PopupComponent
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        title={`Set Targets for ${targetUserName}`}
        size="md"
        loading={submitting}
        footer={
          <div style={{ 
            display: "flex", 
            justifyContent: "flex-end", 
            gap: "12px", 
            padding: "0 8px" 
          }}>
            <button 
              onClick={() => setIsPopupOpen(false)}
              style={{
                padding: "7px 16px",
                backgroundColor: "white",
                color: "#333",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                cursor: "pointer"
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSetTargets}
              disabled={submitting}
              style={{
                padding: "7px 16px",
                backgroundColor: "#1890ff",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                cursor: submitting ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              {submitting ? (
                <>
                  <span style={{ display: "inline-block", width: "14px", height: "14px", border: "2px solid white", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite", marginRight: "8px" }}></span>
                  <span>Saving...</span>
                </>
              ) : (
                "Save Targets"
              )}
            </button>
          </div>
        }
      >
        <div style={{ padding: "16px 24px" }}>
          <div style={{ marginBottom: "24px" }}>
            <label 
              htmlFor="salesTarget" 
              style={{ 
                display: "block", 
                fontSize: "14px", 
                fontWeight: "600", 
                color: "#333", 
                marginBottom: "8px" 
              }}
            >
              Monthly Sales Target
            </label>
            <div style={{ 
              position: "relative", 
              width: "100%" 
            }}>
              <span style={{ 
                position: "absolute", 
                left: "12px", 
                top: "50%", 
                transform: "translateY(-50%)", 
                fontSize: "16px", 
                color: "#666",
                zIndex: 1
              }}>₹</span>
              <input
                id="salesTarget"
                type="text"
                value={salesTarget}
                onChange={(e) => setSalesTarget(e.target.value)}
                style={{ 
                  width: "100%", 
                  padding: "10px 12px 10px 28px", 
                  border: "1px solid #d1d5db", 
                  borderRadius: "6px", 
                  fontSize: "16px",
                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)"
                }}
                placeholder="0"
              />
            </div>
            <div style={{ 
              fontSize: "12px", 
              color: "#6b7280", 
              marginTop: "4px" 
            }}>
              Set the monthly sales revenue target in rupees
            </div>
          </div>
          
          <div style={{ marginBottom: "16px" }}>
            <label 
              htmlFor="callsTarget" 
              style={{ 
                display: "block", 
                fontSize: "14px", 
                fontWeight: "600", 
                color: "#333", 
                marginBottom: "8px" 
              }}
            >
              Monthly Calls Target
            </label>
            <input
              id="callsTarget"
              type="text"
              value={callsTarget}
              onChange={(e) => setCallsTarget(e.target.value)}
              style={{ 
                width: "100%", 
                padding: "10px 12px", 
                border: "1px solid #d1d5db", 
                borderRadius: "6px", 
                fontSize: "16px",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)"
              }}
              placeholder="0"
            />
            <div style={{ 
              fontSize: "12px", 
              color: "#6b7280", 
              marginTop: "4px" 
            }}>
              Set the monthly target for number of calls to make
            </div>
          </div>
        </div>
      </PopupComponent>

      {/* 🎯 NEW: Leads Details Popup */}
      <PopupComponent
        isOpen={leadsPopupOpen}
        onClose={() => setLeadsPopupOpen(false)}
        title={`Sales Details - ${selectedUserName}`}
        size="lg"
        footer={
          <div style={{ 
            display: "flex", 
            justifyContent: "flex-end", 
            padding: "0 8px" 
          }}>
            <button 
              onClick={() => setLeadsPopupOpen(false)}
              style={{
                padding: "8px 20px",
                backgroundColor: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                cursor: "pointer"
              }}
            >
              Close
            </button>
          </div>
        }
      >
        <div style={{ padding: "20px" }}>
          {selectedUserLeads.length > 0 ? (
            <>
              {/* Summary Stats */}
              <LeadsSummaryCard>
                <div className="stat-card">
                  <div className="label">Total Leads</div>
                  <div className="value">{selectedUserLeads.length}</div>
                </div>
                <div className="stat-card">
                  <div className="label">Total Amount</div>
                  <div className="value">
                    {formatCurrency(selectedUserLeads.reduce((sum, lead) => sum + lead.salesAmount, 0))}
                  </div>
                </div>
                <div className="stat-card">
                  <div className="label">Average Sale</div>
                  <div className="value">
                    {formatCurrency(selectedUserLeads.reduce((sum, lead) => sum + lead.salesAmount, 0) / selectedUserLeads.length)}
                  </div>
                </div>
              </LeadsSummaryCard>

              {/* Leads Table */}
              <LeadsPopupTable>
                <div className="table-container">
                  <div className="table-header">
                    <div>Lead Name</div>
                    <div>Amount</div>
                    <div>Sold By</div>
                    <div>Date</div>
                  </div>
                  {selectedUserLeads.map((lead) => (
                    <div key={lead.id} className="table-row">
                      <div className="lead-name">{lead.name}</div>
                      <div className="sales-amount">{formatCurrency(lead.salesAmount)}</div>
                      <div className="sold-by">{lead.soldByName}</div>
                      <div className="sales-date">{moment(lead.salesDate).format('MMM DD, YYYY')}</div>
                    </div>
                  ))}
                </div>
              </LeadsPopupTable>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <h4>No leads found</h4>
              <p>This user hasn't made any sales yet.</p>
            </div>
          )}
        </div>
      </PopupComponent>
    </div>
  );
};

export default SalesTargetTracking;